/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { AuthSessionUnavailable, isSessionVerificationUnavailable } from './session-verification';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { ar } from '../locales/ar';
import { de } from '../locales/de';
import { es } from '../locales/es';
import { fr } from '../locales/fr';
import { it } from '../locales/it';
import { ja } from '../locales/ja';
import { ru } from '../locales/ru';
import { zh } from '../locales/zh';
import axios, { clearAxiosGetCoalesceCache } from '../api/axios';
import { getBrowserTimezone } from '../utils/timezone.utils';
import { getBrowserLanguage, normalizeLanguage, SupportedLanguage, SUPPORTED_LANGUAGES } from '../utils/language.utils';
import { readCapturePresentation } from '../config/capture-presentation.config';
import { ApiConfig, InitialAuth } from '../types/api.types';
import { formatApiValidationErrors } from '../utils/error.utils';
import {
  attachSelectedCompanyIdToRequest,
  clearSelectedCompanyScopeStorage,
  COMPANY_SCOPE_HEADER,
  getValidatedSelectedCompanyIdForRequest,
  setValidatedSelectedCompanyIdForRequest,
} from '../config/company-scope.config';
import { beginAuthenticatedWorkspace, getCompanyScopeSnapshot, setWorkspaceMembershipForRequests, suspendWorkspaceRequests } from '../utils/company-scope-lifecycle.utils';
import { readWorkspaceEntryIntent } from './workspace-navigation.utils';
import { clearWorkspaceSignOutNotice, parseWorkspaceNotice, publishWorkspaceSignOut, readWorkspaceNotice } from './workspace-transition.utils';
import { normalizeUserAuthority } from '../utils/user-authority.utils';
import { applyProductPermissionContext, needsProductPermissionContext } from '../utils/product-permission-context.utils';
import { clearQubeproMediaPreviewCache } from '../dms';
import { withViteBasePathForApiDms, getAppApiBaseUrl } from '../utils/api-same-origin.utils';
import { installQubeProMobileWebViewNavigationPolicy } from '../utils/mobile-webview-navigation.utils';
import { persistUserLanguage } from '../utils/language-persistence.utils';
import { clearScopedMemoryCaches } from '../utils/scoped-memory-cache.utils';
import { invalidateUserGroupDirectory } from '../hooks/useUserGroupDirectory';
import { TenantProvider } from './TenantContext';
import { getApplicationCodeForApi } from '../config/app.config';
import { isApplicationBrowserAuthMode } from '../config/auth-principal-mode.config';
import { ApplicationSpaceChooser } from '../components/auth/ApplicationSpaceChooser';
import {
  COOKIE_ONLY_SESSION_MARKER,
  clearHumanAuthorizationHeader,
  toCookieOnlySessionMarker,
} from '../utils/browser-session-auth.utils';

// Desteklenen herhangi bir dil için çevirileri getiren yardımcı fonksiyon
const translations: Record<SupportedLanguage, Record<string, any>> = {
  tr, en, ar, de, es, fr, it, ja, ru, zh
};

function getTranslations(language: SupportedLanguage): Record<string, any> {
  return translations[language] || translations.en;
}

/** Yerel Vite → API proxy; development otomatik açılmaz (`MODE` yalnızca log/detay için kullanılır). */
const USE_PROXY = import.meta.env.VITE_USE_PROXY === 'true';
const API_PORT = import.meta.env.VITE_API_PORT || (import.meta.env.DEV ? '4001' : '4000');
const withApiPort = (message: string): string => message.replace(/\{apiUrl\}/g, `API port ${API_PORT}`);
const DATABASE_UNAVAILABLE_CODE = 'TENANT_DATABASE_UNAVAILABLE';

function isTruthyHeader(value: unknown): boolean {
  return String(value ?? '').toLowerCase() === 'true';
}

function isDatabaseUnavailableError(error: any): boolean {
  const response = error?.response;
  if (response?.status !== 503) return false;
  const data = response.data ?? {};
  return (
    data.databaseUnavailable === true ||
    data.code === DATABASE_UNAVAILABLE_CODE ||
    isTruthyHeader(response.headers?.['x-database-unavailable'])
  );
}

function appendPublicErrorCode(message: string, data: any): string {
  const publicCode = data?.publicCode || data?.errorCode;
  if (!publicCode || message.includes(String(publicCode))) return message;
  return `${message} Hata kodu: ${publicCode}`;
}

function getDatabaseUnavailableMessage(error: any, language: SupportedLanguage): string {
  const t = getTranslations(language);
  const data = error?.response?.data ?? error?.data ?? error ?? {};
  const message = (
    (import.meta.env.MODE === 'development' ? data?.developerMessage : '') ||
    data?.message ||
    error?.userMessage ||
    (import.meta.env.MODE === 'development' ? t.common?.databaseUnavailableDeveloperHint : '') ||
    t.common?.databaseUnavailableMessage ||
    t.login?.errors?.databaseUnavailable ||
    'Tenant database is unavailable. Please check VPN and database access.'
  );
  return appendPublicErrorCode(message, data);
}

function getDatabaseStatusErrorMessage(statusData: any, language: SupportedLanguage): string {
  const t = getTranslations(language);
  if (statusData?.databaseUnavailable) {
    const message =
      (import.meta.env.MODE === 'development' ? statusData.developerMessage : '') ||
      (import.meta.env.MODE === 'development' ? t.common?.databaseUnavailableDeveloperHint : '') ||
      statusData.message ||
      t.common?.databaseUnavailableMessage ||
      t.login?.errors?.databaseUnavailable ||
      'Tenant database is unavailable.';
    return appendPublicErrorCode(message, statusData);
  }
  return statusData?.message || statusData?.error || t.common?.errorOccurred || 'Veritabanı kurulumu başarısız';
}

// Vite define: çoğu kurulumda production’da `/${APPLICATION_CODE küçük harf}`; kök `.env` BASE_PATH ile istisnai override.
// Boşsa göreli /api ve /dms isteklerine önek eklenmez (dev proxy veya subdomain / kök yayın).
const getInitialBasePath = () => {
  return (import.meta.env.VITE_BASE_PATH || '').replace(/\/+$/, '');
};

export const BASE_PATH = getInitialBasePath();

// Axios credentials ve base URL
axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  if (typeof config.url === 'string') {
    config.url = withViteBasePathForApiDms(config.url);
  }
  return config;
});

axios.interceptors.request.use((config) => {
  attachSelectedCompanyIdToRequest(config);
  return config;
});

if (!USE_PROXY) {
  // Canlı ortamda tüm istekler mevcut domain üzerinden gitmeli
  axios.defaults.baseURL = window.location.origin;
}

// Varsayılan endpoint'ler (case sensitivity düzeltildi - küçük harf)
const DEFAULT_AUTH_LOGIN_PATH = '/api/auth/login';
const DEFAULT_AUTH_REFRESH_PATH = '/api/auth/refresh';
const DEFAULT_AUTH_LOGOUT_PATH = '/api/auth/logout';
const DEFAULT_AUTH_ME_PATH = '/api/auth/me';
const DEFAULT_AUTH_DB_STATUS_PATH = '/api/auth/db-status';
const APPLICATION_AUTH_MODE = isApplicationBrowserAuthMode();
const APPLICATION_AUTH_BASE_PATH = '/api/auth/application';

const LANGUAGE_STORAGE_KEY = 'qubepro_language';

function readSavedLanguage(): SupportedLanguage | null {
  try {
    const v = localStorage.getItem(LANGUAGE_STORAGE_KEY) as SupportedLanguage | null;
    return v && SUPPORTED_LANGUAGES.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function persistLanguage(lang: SupportedLanguage) {
  if (readCapturePresentation()) return;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}

function readTrimmedString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function hasUserIdentityFields(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return Boolean(
    readTrimmedString(
      record.email,
      record.Email,
      record.mail,
      record.userEmail,
      record.firstName,
      record.FirstName,
      record.first_name,
      record.lastName,
      record.LastName,
      record.last_name,
      record.fullName,
      record.FullName,
      record.full_name,
      record.displayName,
      record.display_name,
      record.name,
      record.userName,
      record.username
    )
  );
}

function resolveUserIdentitySource(user: Partial<User> & Record<string, any>): Partial<User> & Record<string, any> {
  const source = user as Record<string, any>;
  if (hasUserIdentityFields(source)) return source;
  const nested = source.user ?? source.profile ?? source.account ?? source.data;
  return hasUserIdentityFields(nested) ? nested : user;
}

function normalizeUserIdentity(user: Partial<User> & Record<string, any>): User {
  const identity = resolveUserIdentitySource(user);
  const rawEmail = readTrimmedString(identity.email, identity.Email, identity.mail, identity.userEmail);
  const rawFirst = readTrimmedString(identity.firstName, identity.FirstName, identity.first_name, identity.givenName, identity.given_name);
  const rawLast = readTrimmedString(identity.lastName, identity.LastName, identity.last_name, identity.surname, identity.familyName, identity.family_name);
  const fullNameSource =
    readTrimmedString(identity.fullName, identity.FullName, identity.full_name, identity.displayName, identity.display_name, identity.name, identity.userName, identity.username);
  const [splitFirst = '', ...rest] = fullNameSource.split(/\s+/).filter(Boolean);
  const splitLast = rest.join(' ').trim();
  const emailLocalPart = rawEmail.includes('@') ? rawEmail.split('@')[0] : rawEmail;
  const emailDerivedFirst = emailLocalPart
    ? emailLocalPart.replace(/[._-]+/g, ' ').trim().split(/\s+/).filter(Boolean)[0] ?? ''
    : '';
  // Core `/auth/me` tenant veritabanı kimliğini `customerId` olarak taşır. UI içinde
  // firma kapsamıyla karışmaması için yalnız customerId -> tenantId normalize edilir;
  // companyId hiçbir zaman tenant yedeği değildir.
  const rawTenantId = identity.tenantId ?? identity.customerId ?? user.tenantId ?? user.customerId;
  const rawUserKind = identity.userKind ?? user.userKind;
  const userKind = rawUserKind === 'EXTERNAL' || rawUserKind === 'INTERNAL' ? rawUserKind : 'UNCLASSIFIED';
  const membershipId = identity.membershipId != null ? String(identity.membershipId) : (user.membershipId != null ? String(user.membershipId) : null);
  const authority = normalizeUserAuthority({
    membershipId, userKind, platformStaff: identity.platformStaff === true || user.platformStaff === true,
    isAdmin: identity.isAdmin ?? user.isAdmin, role: identity.role ?? user.role,
    roles: identity.roles ?? user.roles, permissions: identity.permissions ?? user.permissions,
    customerId: rawTenantId, membershipStatus: identity.membershipStatus,
    tenantRole: identity.tenantRole, managementAccess: identity.managementAccess,
  });

  return {
    id: String(identity.id ?? user.id ?? ''),
    email: rawEmail,
    firstName: rawFirst || splitFirst || emailDerivedFirst || '',
    lastName: rawLast || splitLast || '',
    tenantId: rawTenantId != null ? String(rawTenantId) : null,
    tenantName: identity.tenantName != null ? String(identity.tenantName) : (user.tenantName != null ? String(user.tenantName) : null),
    identityId: identity.identityId != null ? String(identity.identityId) : (user.identityId != null ? String(user.identityId) : null),
    membershipId,
    membershipStatus: identity.membershipStatus === 'ACTIVE' ? 'ACTIVE' : (user.membershipStatus === 'ACTIVE' ? 'ACTIVE' : null),
    tenantRole: typeof identity.tenantRole === 'string' ? identity.tenantRole : (typeof user.tenantRole === 'string' ? user.tenantRole : null),
    authVersion: Number.isInteger(Number(identity.authVersion ?? user.authVersion)) ? Number(identity.authVersion ?? user.authVersion) : null,
    membershipVersion: Number.isInteger(Number(identity.membershipVersion ?? user.membershipVersion)) ? Number(identity.membershipVersion ?? user.membershipVersion) : null,
    tenantAdminScope: (identity.tenantAdminScope ?? user.tenantAdminScope) as User['tenantAdminScope'],
    userKind,
    ...authority,
    language: typeof identity.language === 'string' ? identity.language : (typeof user.language === 'string' ? user.language : null),
    avatar: typeof identity.avatar === 'string' ? identity.avatar : (typeof user.avatar === 'string' ? user.avatar : null),
    ...((identity.settings ?? user.settings) && typeof (identity.settings ?? user.settings) === 'object' && !Array.isArray(identity.settings ?? user.settings)
      ? { settings: (identity.settings ?? user.settings) as Record<string, unknown> }
      : {}),
  };
}

function applyLoginEmailFallback(user: User, loginEmail?: string): User {
  if (user.firstName || user.lastName) return user;
  const email = (loginEmail || '').trim();
  if (!email) return user;
  const local = email.includes('@') ? email.split('@')[0] : email;
  const first = (local.replace(/[._-]+/g, ' ').trim().split(/\s+/).filter(Boolean)[0] ?? '');
  if (!first) return user;
  return { ...user, firstName: first };
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string | null;
  tenantName: string | null;
  identityId?: string | null;
  membershipId?: string | null;
  membershipStatus?: 'ACTIVE' | null;
  tenantRole?: string | null;
  authVersion?: number | null;
  membershipVersion?: number | null;
  platformStaff?: boolean;
  managementAccess?: { kind: 'TENANT_MANAGEMENT'; customerId: string };
  /** Hesap sınıflandırmasıdır; ürün/firma erişimi veya rol yerine kullanılamaz. */
  userKind: 'INTERNAL' | 'EXTERNAL' | 'UNCLASSIFIED';
  roles?: string[];
  role?: string | number;
  permissions: string[];
  isAdmin?: boolean;
  tenantAdminScope?: import('../utils/product-permission-context.utils').TenantAdminScope;
  language: string | null;
  avatar: string | null;
  /** /me endpoint'inden gelen kullanıcı ayarları (appsOrder, theme, pageSize vb.) */
  settings?: Record<string, unknown>;
}

export interface ApplicationSpaceOption {
  membershipId: string;
  spaceId: string;
  applicationCode: string;
  spaceName: string;
  roleCode: string;
  lifecycleRole: 'SPACE_OWNER' | 'SPACE_ADMIN' | null;
  status: 'ACTIVE';
  accessVersion: string;
  isSelected: boolean;
}

function normalizeApplicationSpaces(value: unknown): ApplicationSpaceOption[] {
  if (!Array.isArray(value)) return [];
  const expectedApplicationCode = getApplicationCodeForApi().trim().toUpperCase();
  const seenMembershipIds = new Set<string>();
  const result: ApplicationSpaceOption[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== 'object') continue;
    const item = candidate as Record<string, unknown>;
    const membershipId = String(item.membershipId ?? '').trim();
    const spaceId = String(item.spaceId ?? '').trim();
    const applicationCode = String(item.applicationCode ?? '').trim().toUpperCase();
    const spaceName = String(item.spaceName ?? '').trim();
    const roleCode = String(item.roleCode ?? '').trim().toUpperCase();
    const accessVersion = String(item.accessVersion ?? '').trim();
    if (
      !/^[1-9]\d*$/.test(membershipId)
      || !/^[1-9]\d*$/.test(spaceId)
      || !/^(?:0|[1-9]\d*)$/.test(accessVersion)
      || applicationCode !== expectedApplicationCode
      || !spaceName
      || spaceName.length > 200
      || !/^[A-Z][A-Z0-9_-]{1,79}$/.test(roleCode)
      || item.status !== 'ACTIVE'
      || seenMembershipIds.has(membershipId)
    ) continue;
    const lifecycleRole = item.lifecycleRole === 'SPACE_OWNER' || item.lifecycleRole === 'SPACE_ADMIN'
      ? item.lifecycleRole
      : null;
    seenMembershipIds.add(membershipId);
    result.push({
      membershipId,
      spaceId,
      applicationCode,
      spaceName,
      roleCode,
      lifecycleRole,
      status: 'ACTIVE',
      accessVersion,
      isSelected: item.isSelected === true,
    });
  }
  return result;
}

interface AuthContextType {
  /** A transient verification failure is not a definitive company denial. */
  sessionVerificationUnavailable?: boolean;
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  language: SupportedLanguage;
  dbSize: string | null;
  /** /me'den gelen kullanıcı ayarları (appsOrder, theme, pageSize vb.) — ayrı API çağrısı gerektirmez */
  userSettings: Record<string, unknown> | null;
  databaseInitializing: boolean;
  databaseInitializationMessage: string;
  databaseError: string | null;
  /** Migration çalışıyorsa true; tüm firma kullanıcılarına "Sistem güncelleniyor" gösterilir */
  migrationInProgress: boolean;
  workspaceSelectionRequired: boolean;
  finishSecondFactor: (requiresWorkspaceSelection: boolean) => void;
  applicationSpaceSelectionRequired: boolean;
  applicationSpaces: readonly ApplicationSpaceOption[];
  login: (
    email: string,
    password: string,
    options?: { spaceCode?: string },
  ) => Promise<boolean>;
  logout: () => void;
  updateLanguage: (language: SupportedLanguage) => Promise<void>;
  /** UI bootstrap: dil state + localStorage; PATCH /me/language yapmaz (döngü önleme) */
  applyLanguageLocal: (language: SupportedLanguage) => void;
  refreshUser: () => Promise<User | null>;
  updateUserAvatar: (avatar: string | null) => void;
  updateUserSettingsInContext: (settings: Partial<User>) => void;
  /** Tek bir ayar anahtarını güncelle (PATCH sonrası context sync için) */
  patchUserSettings: (patch: Record<string, unknown>) => void;
  clearDatabaseInitializing: () => void;
  /** Workspace cookie değişmeden önce tenant/company bağlı tüm client state'i sıfırlar. */
  resetForWorkspaceSwitch: () => void;
  /** qpi identity oturumundan bir qpa application-space oturumu seçer. */
  selectApplicationSpace: (membershipId: string) => Promise<void>;
}

const AUTH_CONTEXT_GLOBAL_KEY = 'qubepro_auth_context';

function getStableAuthContext(): React.Context<AuthContextType | undefined> {
  const g = globalThis as typeof globalThis & {
    [AUTH_CONTEXT_GLOBAL_KEY]?: React.Context<AuthContextType | undefined>;
  };
  if (!g[AUTH_CONTEXT_GLOBAL_KEY]) {
    g[AUTH_CONTEXT_GLOBAL_KEY] = createContext<AuthContextType | undefined>(undefined);
  }
  return g[AUTH_CONTEXT_GLOBAL_KEY]!;
}

const AuthContext = getStableAuthContext();

function clearDefaultAuthorizationHeader(): void {
  delete axios.defaults.headers.common['Authorization'];
  delete axios.defaults.headers.common['authorization'];
}

export interface AuthProviderProps {
  initialAuth?: InitialAuth;
  apiConfig?: ApiConfig;
  onLogin?: (authData: any) => void;
  onLogout?: () => void;
  onTokenChange?: (authData: any) => void;
  children: React.ReactNode;
}

// ── 401 kurtarma işlemi için modül seviyesinde mutex ──
// Eşzamanlı kurtarma girişimlerinin birbiriyle yarışmasını önler.
// React yeniden render işlemlerinden kurtulması için bileşenin dışında olmalıdır.
let _isRecovering401 = false;
let _recoveryPromise: Promise<string | null> | null = null;
// Son başarılı recovery zamanı - cascade'i önlemek için (ms)
let _lastRecoveryTime = 0;
// StrictMode çift-invocation guard: initializeAuth aynı anda iki kez çalışmasın
let _initializeAuthRunning = false;
const AUTH_ME_CACHE_MS = 30000;
const AUTH_INIT_WATCHDOG_MS = 8000;
const LOCAL_ACCOUNT_LOCKED_CODE = 'LOCAL_ACCOUNT_LOCKED';
let _lastAuthMeSnapshot: { at: number; companyScopeRevision?: number; user: User; dbSize: string | null; settings: Record<string, unknown> | null } | null = null;

export function AuthProvider({
  children,
  initialAuth,
  apiConfig,
  onLogin,
  onLogout,
  onTokenChange,
}: AuthProviderProps) {
  useEffect(() => installQubeProMobileWebViewNavigationPolicy(), []);

  const debugAuthTiming = useCallback((_phase: string, _extra?: Record<string, unknown>) => {
    // AUTH timing logs intentionally disabled to keep console clean.
  }, []);

  // Legacy initialAuth token değeri bilinçli olarak yok sayılır. Browser
  // yalnız HttpOnly cookie'yi kullanır; state credential olmayan marker taşır.
  const signedOutAtMount = parseWorkspaceNotice(readWorkspaceNotice())?.phase === 'signed-out';
  const explicitAccountChange = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('account') === 'change';
  const initialToken = !signedOutAtMount && !explicitAccountChange && initialAuth?.isAuthenticated && initialAuth?.user
    ? toCookieOnlySessionMarker(initialAuth.token)
    : null;
  const initialUser = ((): User | null => {
    if (signedOutAtMount || explicitAccountChange) return null;
    if (initialAuth?.user) return initialAuth.user as User;
    return null;
  })();

  // API endpoint'lerini config'den al veya varsayılanları kullan (belleğe alınmış)
  const loginEndpoint = useMemo(() => {
    return apiConfig?.endpoints?.login
      || (APPLICATION_AUTH_MODE ? `${APPLICATION_AUTH_BASE_PATH}/login` : DEFAULT_AUTH_LOGIN_PATH);
  }, [apiConfig?.endpoints?.login]);

  const refreshEndpoint = useMemo(() => {
    return apiConfig?.endpoints?.refresh
      || (APPLICATION_AUTH_MODE ? `${APPLICATION_AUTH_BASE_PATH}/no-refresh` : DEFAULT_AUTH_REFRESH_PATH);
  }, [apiConfig?.endpoints?.refresh]);

  const logoutEndpoint = useMemo(() => {
    return apiConfig?.endpoints?.logout
      || (APPLICATION_AUTH_MODE ? `${APPLICATION_AUTH_BASE_PATH}/logout` : DEFAULT_AUTH_LOGOUT_PATH);
  }, [apiConfig?.endpoints?.logout]);

  const meEndpoint = useMemo(() => {
    return apiConfig?.endpoints?.me || (APPLICATION_AUTH_MODE
      ? `${APPLICATION_AUTH_BASE_PATH}/identity-summary?applicationCode=${encodeURIComponent(getApplicationCodeForApi())}`
      : DEFAULT_AUTH_ME_PATH);
  }, [apiConfig?.endpoints?.me]);

  const languageEndpoint = useMemo(() => {
    return apiConfig?.endpoints?.language?.trim() || null;
  }, [apiConfig?.endpoints?.language]);

  // API base URL'ini config'den al veya mevcut mantığı kullan
  const apiBaseUrl = useMemo(() => {
    if (apiConfig?.baseURL) {
      return apiConfig.baseURL.replace(/\/+$/, '');
    }
    return getAppApiBaseUrl();
  }, [apiConfig?.baseURL]);

  // buildApiUrl'i güncelle (belleğe alınmış)
  const buildApiUrlWithConfig = useCallback((path: string): string => {
    if (!apiBaseUrl) return path;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${apiBaseUrl}${normalizedPath}`;
  }, [apiBaseUrl]);

  const loginEndpointRef = React.useRef(loginEndpoint);
  const refreshEndpointRef = React.useRef(refreshEndpoint);
  const buildApiUrlWithConfigRef = React.useRef(buildApiUrlWithConfig);
  loginEndpointRef.current = loginEndpoint;
  refreshEndpointRef.current = refreshEndpoint;
  buildApiUrlWithConfigRef.current = buildApiUrlWithConfig;

  // Daha önce kurulmuş host/default header'ları render başında da fail-closed temizle.
  clearDefaultAuthorizationHeader();

  const [user, setUser] = useState<User | null>(initialUser);
  const [sessionVerificationUnavailable, setSessionVerificationUnavailable] = useState(false);
  React.useLayoutEffect(() => {
    setWorkspaceMembershipForRequests(user?.membershipId ? String(user.membershipId) : null);
  }, [user?.membershipId]);
  const [token, setToken] = useState<string | null>(initialToken);
  // tokenRef: Interceptor closure'larının her zaman güncel token'ı okuması için.
  // React state güncellemesi async olduğundan, useEffect re-run olmadan önce
  // yeni istekler eski closure'daki stale token'ı okurdu. Ref bunu çözer.
  const tokenRef = React.useRef<string | null>(initialToken);
  /** apiConfig her render'da yeni referans olabilir; callback/interceptor bağımlılığında kullanılır */
  const apiConfigRef = React.useRef(apiConfig);
  apiConfigRef.current = apiConfig;
  /** Eşzamanlı GET /auth/me çağrılarını tek promise'te birleştir */
  const fetchCurrentUserInFlightRef = React.useRef<Promise<User | null> | null>(null);
  const fetchCurrentUserScopeRevisionRef = React.useRef<number | undefined>(undefined);
  const refreshAccessTokenRef = React.useRef<(() => Promise<boolean>) | null>(null);
  /** Oturum başına initializeAuth yalnızca bir kez (initialAuth kimliği değişmedikçe) */
  const authInitIdentityRef = React.useRef<string>('');
  const authInitCompletedRef = React.useRef(false);
  /** token/user effect: aynı token için tekrar /me denemesini önler */
  const meHydrateTokenRef = React.useRef<string | null>(null);
  /** Oturum başına tek /me hydrate (logout'ta sıfırlanır) */
  const meSessionHydratedRef = React.useRef(false);
  /** Logout sonrası geç async yanıtların state yazmasını engeller */
  const authSessionEpochRef = React.useRef(0);
  const authRequestsRef = React.useRef(new AbortController());
  const logoutInFlightRef = React.useRef<Promise<void> | null>(null);
  const secondFactorEpochRef = React.useRef<number | null>(null);
  const refreshFlightRef = React.useRef<{ epoch: number; promise: Promise<boolean> } | null>(null);
  const [, setIsRefreshing] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  // Database initialization state'leri — sadece memory'de tutulur (hassas olmasa da localStorage gerekmez)
  const [databaseInitializing, setDatabaseInitializing] = useState<boolean>(false);
  const [databaseInitializationMessage, setDatabaseInitializationMessage] = useState<string>('');
  const [databaseError, setDatabaseError] = useState<string | null>(null);
  // DB boyutu — /auth/me response'undan memory'de tutulur, Dashboard context'ten okur
  const [dbSize, setDbSize] = useState<string | null>(null);
  // Kullanıcı ayarları — /me response'undaki settings objesi (appsOrder, theme vb.)
  const [userSettings, setUserSettings] = useState<Record<string, unknown> | null>(null);
  const [migrationInProgress, setMigrationInProgress] = useState<boolean>(false);
  const [workspaceSelectionRequired, setWorkspaceSelectionRequired] = useState<boolean>(false);
  const [applicationSpaceSelectionRequired, setApplicationSpaceSelectionRequired] = useState<boolean>(false);
  const [applicationSpaces, setApplicationSpaces] = useState<ApplicationSpaceOption[]>([]);
  const applicationSpacesRef = React.useRef<ApplicationSpaceOption[]>([]);
  applicationSpacesRef.current = applicationSpaces;
  const setMigrationInProgressRef = React.useRef(setMigrationInProgress);
  setMigrationInProgressRef.current = setMigrationInProgress;
  /** DB kurulum polling interval handle'ı; bileşen ömrü boyunca tek aktif polling sağlar */
  const dbStatusPollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const loginStartMsRef = React.useRef<number | null>(null);

  // Dil: kullanıcıdan, sonra localStorage'dan, sonra tarayıcıdan alınır; yedeği DEFAULT_LANGUAGE'dir.
  const capturePresentation = useMemo(() => readCapturePresentation(), []);
  const [language, setEffectiveLanguage] = useState<SupportedLanguage>(() => {
    if (capturePresentation?.mode === 'CORE_LOCALE') return capturePresentation.locale;
    if (initialUser?.language) {
      return normalizeLanguage(initialUser.language);
    }
    const savedLanguage = readSavedLanguage();
    if (savedLanguage) return savedLanguage;
    return getBrowserLanguage();
  });
  // Every bootstrap/profile/login path retains its normal semantics outside a
  // capture. Inside a capture, do not even execute persistence updater closures.
  const setLanguage = useCallback<React.Dispatch<React.SetStateAction<SupportedLanguage>>>((next) => {
    setEffectiveLanguage(previous => capturePresentation?.mode === 'CORE_LOCALE' ? capturePresentation.locale : (typeof next === 'function' ? next(previous) : next));
  }, [capturePresentation]);
  // languageRef: Interceptor closure'larının her zaman güncel language'ı okuması için.
  // language state değiştiğinde interceptor eject/re-register olmasın diye ref kullanıyoruz.
  const languageRef = React.useRef<string>(language);
  languageRef.current = language;

  const applyLanguageLocal = useCallback((newLanguage: SupportedLanguage) => {
    const normalized = normalizeLanguage(newLanguage);
    setLanguage((prev) => {
      if (prev === normalized) return prev;
      persistLanguage(normalized);
      return normalized;
    });
  }, []);

  const resetForWorkspaceSwitch = useCallback(() => {
    suspendWorkspaceRequests();
    authSessionEpochRef.current += 1;
    authRequestsRef.current.abort(); authRequestsRef.current = new AbortController();
    refreshFlightRef.current = null;
    secondFactorEpochRef.current = null;
    _isRecovering401 = false; _recoveryPromise = null;
    _lastRecoveryTime = 0;
    if (dbStatusPollRef.current) { clearInterval(dbStatusPollRef.current); dbStatusPollRef.current = null; }
    clearAxiosGetCoalesceCache();
    clearScopedMemoryCaches();
    invalidateUserGroupDirectory();
    clearQubeproMediaPreviewCache();
    setValidatedSelectedCompanyIdForRequest(null);
    clearSelectedCompanyScopeStorage();
    _lastAuthMeSnapshot = null;
    fetchCurrentUserInFlightRef.current = null;
    meSessionHydratedRef.current = false;
    setToken(null);
    tokenRef.current = null;
    setUser(null);
    setDbSize(null);
    setUserSettings(null);
    setWorkspaceSelectionRequired(false);
    applicationSpacesRef.current = [];
    setApplicationSpaces([]);
    setApplicationSpaceSelectionRequired(false);
    clearDefaultAuthorizationHeader();
  }, []);

  const logout = useCallback((): Promise<void> => {
    if (logoutInFlightRef.current) return logoutInFlightRef.current;
    resetForWorkspaceSwitch();
    publishWorkspaceSignOut();
    setIsInitialized(false);
    const operation = (async () => {
      meSessionHydratedRef.current = false;
      clearAxiosGetCoalesceCache();
      // Güvenlik nedeniyle backend logout endpoint'ini çağır ki httpOnly cookie silinsin.
      // Await kullanarak, yanıt dönmeden kullanıcının tekrar login olmasını engelliyor ve
      // eski cookie'nin yeni login cookie'sini ezmesini önlüyoruz.
      try {
        if (logoutEndpoint) {
          await axios.post(
            buildApiUrlWithConfig(logoutEndpoint),
            {},
            { headers: apiConfigRef.current?.getHeaders?.(), withCredentials: true, timeout: 15000 }
          );
        }
      } catch (e) {
        if (import.meta.env.MODE === 'development') {
          console.warn('Backend logout call failed, forcing client-side logout anyway.', e);
        }
      }

      setToken(null);
      tokenRef.current = null;
      setUser(null);
      setDbSize(null);
      authInitCompletedRef.current = false;
      authInitIdentityRef.current = '';
      meHydrateTokenRef.current = null;
      fetchCurrentUserInFlightRef.current = null;
      _lastAuthMeSnapshot = null;
      setIsInitialized(true);
      setDatabaseInitializing(false);
      setDatabaseInitializationMessage('');
      setDatabaseError(null);
      setMigrationInProgress(false);
      setWorkspaceSelectionRequired(false);
      applicationSpacesRef.current = [];
      setApplicationSpaces([]);
      setApplicationSpaceSelectionRequired(false);

      clearQubeproMediaPreviewCache();



      // Authorization header'ı kaldır
      clearDefaultAuthorizationHeader();

      // onLogout callback'i çağır
      if (onLogout) {
        onLogout();
      }

      // ProtectedRoute isAuthenticated false olunca login'e yönlendirecek.
    })();
    logoutInFlightRef.current = operation;
    void operation.finally(() => { if (logoutInFlightRef.current === operation) logoutInFlightRef.current = null; }).catch(() => {});
    return operation;
  }, [onLogout, resetForWorkspaceSwitch, logoutEndpoint, buildApiUrlWithConfig]);


  const fetchCurrentUser = useCallback(async (): Promise<User | null> => {
    const companyScope = getCompanyScopeSnapshot();
    const companyScopeRevision = APPLICATION_AUTH_MODE ? undefined : companyScope.revision;
    const companyId = APPLICATION_AUTH_MODE ? null : getValidatedSelectedCompanyIdForRequest();
    const permissionScope = { appCode: getApplicationCodeForApi(), companyId, applicationAuthMode: APPLICATION_AUTH_MODE };
    const isCurrentScope = () => APPLICATION_AUTH_MODE
      || (!companyScope.signal.aborted && getCompanyScopeSnapshot().revision === companyScopeRevision);
    if (fetchCurrentUserInFlightRef.current && fetchCurrentUserScopeRevisionRef.current === companyScopeRevision) {
      return fetchCurrentUserInFlightRef.current;
    }

    if (_lastAuthMeSnapshot && _lastAuthMeSnapshot.companyScopeRevision === companyScopeRevision
      && Date.now() - _lastAuthMeSnapshot.at < AUTH_ME_CACHE_MS) {
      setDbSize(_lastAuthMeSnapshot.dbSize);
      setUserSettings(_lastAuthMeSnapshot.settings);
      setUser(_lastAuthMeSnapshot.user);
      return _lastAuthMeSnapshot.user;
    }

    const run = async (): Promise<User | null> => {
    const sessionEpoch = authSessionEpochRef.current;
    try {
      const startedAt = Date.now();
      debugAuthTiming('fetchCurrentUser:start', { endpoint: meEndpoint });
      const headers = apiConfigRef.current?.getHeaders?.() || {};
      const noCacheHeaders = { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };
      const requestConfig = {
        // Bootstrap stays company-free. After CompanyProvider validates a
        // selection, /me must resolve that company's current role permissions.
        headers: { ...headers, ...noCacheHeaders, ...(companyId ? { [COMPANY_SCOPE_HEADER]: companyId } : {}) },
        signal: !APPLICATION_AUTH_MODE ? AbortSignal.any([companyScope.signal, authRequestsRef.current.signal]) : authRequestsRef.current.signal,
      };
      // /auth/me can be the shared Core identity profile. Product menus must
      // use Core's existing server-authorized company + application context.
      // Both reads share the cancellation boundary and publish atomically.
      const [response, permissionResponse] = await Promise.all([
        axios.get(buildApiUrlWithConfig(meEndpoint), requestConfig),
        needsProductPermissionContext(permissionScope)
          ? axios.get(buildApiUrlWithConfig(`/api/users/me/company-access/${companyId}`), {
              ...requestConfig, params: { appCode: permissionScope.appCode, permissionContext: '1' },
            })
          : Promise.resolve(null),
      ]);
      if (sessionEpoch !== authSessionEpochRef.current || !isCurrentScope()) return null;
      if (response.data?.success && response.data?.data) {
        setSessionVerificationUnavailable(false);
        const currentUser = response.data.data as User & { dbSize?: string; settings?: Record<string, unknown> };

        // dbSize context'te memory'de tutulur (localStorage değil)
        const { dbSize: fetchedDbSize, settings: fetchedSettings, ...userWithoutExtras } = currentUser;
        if (fetchedDbSize) setDbSize(fetchedDbSize);
        // /me response'undaki settings objesi (appsOrder, theme, pageSize vb.)
        if (fetchedSettings && typeof fetchedSettings === 'object') setUserSettings(fetchedSettings);
        const normalizedUser = applyProductPermissionContext(
          normalizeUserIdentity(userWithoutExtras), permissionResponse?.data, permissionScope,
        );
        setWorkspaceMembershipForRequests(normalizedUser.membershipId ? String(normalizedUser.membershipId) : null);
        setUser(normalizedUser);
        setWorkspaceSelectionRequired(false);
        _lastAuthMeSnapshot = {
          at: Date.now(),
          companyScopeRevision,
          user: normalizedUser,
          dbSize: fetchedDbSize ?? null,
          settings: fetchedSettings && typeof fetchedSettings === 'object' ? fetchedSettings : null,
        };
        setDatabaseError(null);
        setDatabaseInitializing(false);
        setDatabaseInitializationMessage('');
        meSessionHydratedRef.current = true;
        debugAuthTiming('fetchCurrentUser:success', {
          durationMs: Date.now() - startedAt,
          firstName: normalizedUser.firstName || '(empty)',
          lastName: normalizedUser.lastName || '(empty)',
        });
        if (currentUser.language) {
          const userLanguage = normalizeLanguage(currentUser.language);
          setLanguage((prev) => {
            if (prev === userLanguage) return prev;
            persistLanguage(userLanguage);
            return userLanguage;
          });
        }

        return normalizedUser;
      }
      throw new Error('Session verification response is invalid.');
    } catch (error: any) {
      if (sessionEpoch !== authSessionEpochRef.current || !isCurrentScope()) return null;
      if (needsProductPermissionContext(permissionScope)) {
        _lastAuthMeSnapshot = null;
        setUser(previous => previous ? { ...previous, permissions: [], tenantAdminScope: undefined } : previous);
      }
      // 401/403 hatalarını konsola basma, bunlar normal yetki kontrolü süreçleridir
      const status = error?.response?.status || error?.status;
      setSessionVerificationUnavailable(isSessionVerificationUnavailable(error));
      if (isDatabaseUnavailableError(error)) {
        const databaseUnavailableMessage = getDatabaseUnavailableMessage(error, languageRef.current as SupportedLanguage);
        setDatabaseError(databaseUnavailableMessage);
        setDatabaseInitializing(false);
        setDatabaseInitializationMessage('');
        setMigrationInProgress(false);
        error.userMessage = databaseUnavailableMessage;
        error.message = databaseUnavailableMessage;
        if (apiConfigRef.current?.onError) {
          apiConfigRef.current.onError(error);
        }
        return null;
      }
      if (import.meta.env.MODE === 'development' && status !== 401 && status !== 403) {
        console.error('Mevcut kullanıcı çekilemedi:', error);
      }
      // Ağ hatası kontrolü - backend'e bağlanılamıyorsa logout yapma
      if (!error?.response) {
        if (import.meta.env.MODE === 'development') {
          console.warn('Kullanıcı çekilirken ağ hatası - backend kullanılamıyor olabilir');
        }
        // Ağ hatası durumunda, initialAuth'dan kullanıcı bilgisi varsa onu koru
        // Bu durumda null döndürme, çünkü initializeAuth içinde initialAuth kontrolü yapılacak
        return null;
      }
      if (error?.response?.status === 401) {
        // Token cookie'de, JavaScript'ten okunamaz, bu yüzden token kontrolü yapmıyoruz
        // Backend cookie'yi kontrol ediyor, 401/403 dönerse cookie geçersiz/yok demektir
        // ANCAK: initialAuth'dan user bilgisi geldiyse, onu koru
        if (apiConfigRef.current?.onTokenExpired) {
          apiConfigRef.current.onTokenExpired();
        }
        // logout() çağırma, çünkü zaten cookie geçersiz/yok
        // initialAuth kontrolü initializeAuth içinde yapılacak
        return null;
      }

      if (apiConfigRef.current?.onError) {
        apiConfigRef.current.onError(error);
      }
    }
    return null;
    };

    const promise = run();
    fetchCurrentUserScopeRevisionRef.current = companyScopeRevision;
    fetchCurrentUserInFlightRef.current = promise;
    try {
      return await promise;
    } finally {
      if (fetchCurrentUserInFlightRef.current === promise) {
        fetchCurrentUserInFlightRef.current = null;
      }
    }
  }, [meEndpoint, buildApiUrlWithConfig, debugAuthTiming]);

  /** Login sonrası tek /me (dbSize/settings); tekrarlayan hydrate çağrılarını birleştirir */
  const hydrateMeOnceAfterLogin = useCallback(async (): Promise<User | null> => {
    if (meSessionHydratedRef.current) return _lastAuthMeSnapshot?.user ?? null;
    return fetchCurrentUser();
  }, [fetchCurrentUser]);

  const establishApplicationBrowserSession = useCallback((rawUser: unknown): User => {
    if (!rawUser || typeof rawUser !== 'object') {
      throw new Error('Uygulama kimlik yanıtı geçersizdir.');
    }
    const record = rawUser as Record<string, unknown>;
    if (
      String(record.applicationCode ?? '').trim().toUpperCase() !== getApplicationCodeForApi().trim().toUpperCase()
      || !/^[1-9]\d*$/.test(String(record.identityId ?? '').trim())
      || !/^[1-9]\d*$/.test(String(record.membershipId ?? record.applicationMembershipId ?? '').trim())
      || !/^[1-9]\d*$/.test(String(record.spaceId ?? '').trim())
    ) {
      throw new Error('Uygulama kimlik kapsamı uyuşmuyor.');
    }
    const normalizedUser = normalizeUserIdentity(record as Partial<User> & Record<string, any>);
    authSessionEpochRef.current += 1;
    clearAxiosGetCoalesceCache();
    _lastAuthMeSnapshot = {
      at: Date.now(),
      user: normalizedUser,
      dbSize: null,
      settings: null,
    };
    meSessionHydratedRef.current = true;
    fetchCurrentUserInFlightRef.current = null;
    setUser(normalizedUser);
    setDbSize(null);
    setUserSettings(null);
    setToken(COOKIE_ONLY_SESSION_MARKER);
    tokenRef.current = COOKIE_ONLY_SESSION_MARKER;
    clearDefaultAuthorizationHeader();
    setWorkspaceSelectionRequired(false);
    setApplicationSpaces([]);
    setApplicationSpaceSelectionRequired(false);
    setDatabaseError(null);
    setDatabaseInitializing(false);
    setDatabaseInitializationMessage('');
    setIsInitialized(true);
    return normalizedUser;
  }, []);

  const selectApplicationSpace = useCallback(async (membershipIdInput: string): Promise<void> => {
    if (logoutInFlightRef.current) return;
    const sessionEpoch = authSessionEpochRef.current;
    const membershipId = String(membershipIdInput ?? '').trim();
    if (!/^[1-9]\d*$/.test(membershipId)) {
      throw new Error('Uygulama üyeliği geçersizdir.');
    }
    const listedSpaces = applicationSpacesRef.current;
    if (listedSpaces.length > 0 && !listedSpaces.some((space) => space.membershipId === membershipId)) {
      throw new Error('Uygulama üyeliği seçim listesinde bulunamadı.');
    }
    const response = await axios.post(
      buildApiUrlWithConfig(`${APPLICATION_AUTH_BASE_PATH}/spaces/select`),
      { membershipId },
      {
        headers: apiConfigRef.current?.getHeaders?.() || {},
        withCredentials: true,
        signal: authRequestsRef.current.signal,
      },
    );
    if (sessionEpoch !== authSessionEpochRef.current) return;
    const payload = response.data?.data ?? response.data;
    const normalizedUser = establishApplicationBrowserSession(payload?.user);
    if (onLogin) onLogin({ token: COOKIE_ONLY_SESSION_MARKER, user: normalizedUser });
    if (onTokenChange) onTokenChange({ token: COOKIE_ONLY_SESSION_MARKER, user: normalizedUser });
  }, [buildApiUrlWithConfig, establishApplicationBrowserSession, onLogin, onTokenChange]);

  const discoverApplicationSpaces = useCallback(async (): Promise<boolean> => {
    if (!APPLICATION_AUTH_MODE) return false;
    const sessionEpoch = authSessionEpochRef.current;
    try {
      const response = await axios.get(
        buildApiUrlWithConfig(`${APPLICATION_AUTH_BASE_PATH}/spaces`),
        {
          headers: {
            ...(apiConfigRef.current?.getHeaders?.() || {}),
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
          withCredentials: true,
          signal: authRequestsRef.current.signal,
        },
      );
      if (sessionEpoch !== authSessionEpochRef.current) return false;
      const payload = response.data?.data ?? response.data;
      const spaces = normalizeApplicationSpaces(payload?.applicationSpaces);
      if (spaces.length === 0) return false;
      applicationSpacesRef.current = spaces;
      setApplicationSpaces(spaces);
      if (spaces.length === 1) {
        await selectApplicationSpace(spaces[0].membershipId);
      } else {
        setApplicationSpaceSelectionRequired(true);
        setIsInitialized(true);
      }
      return true;
    } catch {
      return false;
    }
  }, [buildApiUrlWithConfig, selectApplicationSpace]);

  // Token yenileme fonksiyonu
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    if (APPLICATION_AUTH_MODE) return false;
    if (logoutInFlightRef.current) return false;
    const sessionEpoch = authSessionEpochRef.current;
    // Login olmadan (token yokken) refresh denemek 400 spam üretir. Cookie tabanlı oturum varsa zaten /me ile anlaşılır.
    if (!tokenRef.current) {
      return false;
    }
    if (refreshFlightRef.current?.epoch === sessionEpoch) return refreshFlightRef.current.promise;

    // Platform session cookie httpOnly olduğu için storage'dan okunamaz; credentials ile cookie gönderilir
    setIsRefreshing(true);
    const operation = (async (): Promise<boolean> => {
      try {
        const headers = apiConfigRef.current?.getHeaders?.() || {};
        const response = await axios.post(buildApiUrlWithConfigRef.current(refreshEndpointRef.current), {}, { headers, signal: authRequestsRef.current.signal });
        if (sessionEpoch !== authSessionEpochRef.current) return false;

        if (response.data.success) {
          const sessionMarker = toCookieOnlySessionMarker(response.data?.data?.token);
          setToken(sessionMarker);
          tokenRef.current = sessionMarker;
          clearDefaultAuthorizationHeader();
          setIsRefreshing(false);
          return true;

          // LOCAL ve opak-cookie oturumlarında refresh yanıtı yeni access token
          // taşımaz. Backend 200/success döndürdüyse HttpOnly session cookie
          // doğrulanmıştır; bunu başarısız saymak, opsiyonel bir uydu 401'inde
          // sağlam Core oturumunun yanlışlıkla kapatılmasına neden olur.
        }

        // Başarısız refresh: logout çağırma, sadece false döndür.
        // logout kararı 401 interceptor tarafından verilecek.
        setIsRefreshing(false);
        return false;
      } catch (error: any) {
        // 400/401 = token yok (çıkış sonrası login sayfası) - beklenen durum, loglama
        const status = error?.response?.status;
        if (import.meta.env.MODE === 'development' && status !== 400 && status !== 401) {
          console.error('Oturum yenileme başarısız:', {
            status,
            message: error?.message,
          });
        }
        // Session cookie ile yenileme başarısız; logout çağırma.
        // logout kararı 401 interceptor tarafından verilecek (çift logout ve döngüsel 401'i önler).
        setIsRefreshing(false);
        return false;
      }
    })();
    refreshFlightRef.current = { epoch: sessionEpoch, promise: operation };
    void operation.finally(() => {
      if (refreshFlightRef.current?.promise === operation) refreshFlightRef.current = null;
    });
    return operation;
  }, []);

  refreshAccessTokenRef.current = refreshAccessToken;

  // Axios request interceptor - Her isteğe token ekle (public route'lar hariç)
  useEffect(() => {
    // Global olarak cookie gönderimini etkinleştir
    // Bu, interceptor geçişleri sırasında bile cookie'nin gönderilmesini sağlar
    axios.defaults.withCredentials = true;

    // Migration overlay: sadece backend'den gelen explicit sinyal ile gösterilir (tahmine dayalı timer yok)
    const OVERLAY_MAX_MS = 60_000;
    let overlaySafetyTimerId: ReturnType<typeof setTimeout> | null = null;
    const clearOverlaySafety = () => {
      if (overlaySafetyTimerId != null) {
        clearTimeout(overlaySafetyTimerId);
        overlaySafetyTimerId = null;
      }
    };

    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        (config as any)._authSessionEpoch = authSessionEpochRef.current;
        if (!config.headers) {
          config.headers = {} as any;
        }
        config.headers['Accept-Language'] = `${languageRef.current},en-US;q=0.9`;

        const urlLower = (config.url || '').toLowerCase();
        const isPublicRoute = ((): boolean => {
          const path = urlLower.split('?')[0];
          const publicPaths = [
            loginEndpointRef.current.toLowerCase(),
            refreshEndpointRef.current.toLowerCase(),
            '/api/auth/register',
            '/health',
            '/api/health'
          ];
          return publicPaths.some(p => path === p.toLowerCase() || path.endsWith(p.toLowerCase())) && !path.includes('/api/system/');
        })();

        if (!isPublicRoute) {
          // Cookie'lerin her zaman gönderilmesini sağla
          config.withCredentials = true;

          // Custom headers ekle (apiConfig ref: uydu projelerde inline nesne her render yenilendiğinde interceptor fırtınası olmasın)
          const customHeaders = apiConfigRef.current?.getHeaders?.();
          if (customHeaders && config.headers) {
            Object.keys(customHeaders).forEach(key => {
              config.headers.set(key, customHeaders[key]);
            });
          }
          // Custom header kaynağı Authorization taşısa dahi insan istekleri
          // cookie-only kalır. Temizleme bilinçli olarak merge sonrasındadır.
          clearHumanAuthorizationHeader(config.headers);
        } else {
          // Public route: Authorization gönderme
          if (config.headers) {
            clearHumanAuthorizationHeader(config.headers);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Axios response interceptor - 401/403 hatalarını token yenileme ile yönet
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        if ((response.config as any)?._authSessionEpoch !== undefined && (response.config as any)._authSessionEpoch !== authSessionEpochRef.current) throw new axios.CanceledError('Auth session changed');
        const fromHeader = response.headers?.['x-migration-in-progress'];
        const fromBody = response.data?.data?.migrationInProgress ?? response.data?.migrationInProgress;
        if (fromHeader === 'true' || fromBody === true) {
          setMigrationInProgressRef.current(true);
          clearOverlaySafety();
          overlaySafetyTimerId = setTimeout(() => {
            overlaySafetyTimerId = null;
            setMigrationInProgressRef.current(false);
          }, OVERLAY_MAX_MS);
        } else if (fromHeader === 'false' || fromBody === false) {
          setMigrationInProgressRef.current(false);
          clearOverlaySafety();
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const responseEpoch = originalRequest?._authSessionEpoch ?? authSessionEpochRef.current;
        if (axios.isCancel(error) || originalRequest?._authSessionEpoch !== undefined && originalRequest._authSessionEpoch !== authSessionEpochRef.current) return Promise.reject(error);

        // Ağ hatası - sunucuya bağlanılamadı / timeout
        if (!error.response) {
          const t = getTranslations(languageRef.current as SupportedLanguage);
          // Proxy error veya connection refused hatası kontrolü
          let networkErrorMessage;
          if (error.code === 'ECONNABORTED') {
            // Axios timeout
            networkErrorMessage = t.common.requestTimeout || 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
          } else if (error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT' || error.message?.includes('Proxy error')) {
            networkErrorMessage = import.meta.env.MODE === 'development'
              ? withApiPort(t.common.backendConnectionErrorDev)
              : t.common.backendConnectionErrorProd;
          } else {
            networkErrorMessage = t.errors.network;
          }

          // Error objesine mesaj ekle (eğer yoksa veya genel network hatası ise)
          if (!error.message || error.message === 'Network Error' || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
            error.userMessage = networkErrorMessage;
            // message'ı da güncelle ki mevcut catch blokları çalışsın
            error.message = networkErrorMessage;
          }

          // Development modunda detaylı log
          if (import.meta.env.MODE === 'development') {
            console.error('❌ Network error:', {
              message: error.message,
              code: error.code,
              url: originalRequest?.url,
              method: originalRequest?.method,
            });
          }

          return Promise.reject(error);
        }

        // 503 Service Unavailable - tenant DB erişimi, migration veya bakım
        if (error.response?.status === 503) {
          const t = getTranslations(languageRef.current as SupportedLanguage);
          if (isDatabaseUnavailableError(error)) {
            const databaseUnavailableMessage = getDatabaseUnavailableMessage(error, languageRef.current as SupportedLanguage);
            setDatabaseError(databaseUnavailableMessage);
            setDatabaseInitializing(false);
            setDatabaseInitializationMessage('');
            setMigrationInProgressRef.current(false);
            clearOverlaySafety();
            error.userMessage = databaseUnavailableMessage;
            error.message = databaseUnavailableMessage;

            if (apiConfigRef.current?.onError) {
              apiConfigRef.current.onError(error);
            }

            return Promise.reject(error);
          }

          const fromBody = error.response?.data?.migrationInProgress;
          const isMigrating = fromBody === true || error.response?.headers?.['x-migration-in-progress'] === 'true';
          setMigrationInProgressRef.current(isMigrating);
          if (isMigrating) {
            clearOverlaySafety();
            overlaySafetyTimerId = setTimeout(() => {
              overlaySafetyTimerId = null;
              setMigrationInProgressRef.current(false);
            }, OVERLAY_MAX_MS);
          }
          const errorData = error.response?.data;
          const serviceUnavailableMessage = errorData?.error || errorData?.message || t.common.systemUpdating;
          error.userMessage = serviceUnavailableMessage;
          error.message = serviceUnavailableMessage;

          if (apiConfigRef.current?.onError) {
            apiConfigRef.current.onError(error);
          }

          return Promise.reject(error);
        }

        // 401 durumunda: session/access token recovery için refresh dene
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Login olmadan (token/cookie yokken) refresh denemek 400 spam üretir; doğrudan 401 döndür.
          if (!tokenRef.current) {
            return Promise.reject(error);
          }
          const urlLower = (originalRequest.url || '').toLowerCase();
          const isPublicRoute = ((): boolean => {
            const path = urlLower.split('?')[0];
            const publicPaths = [
              loginEndpointRef.current.toLowerCase(),
              refreshEndpointRef.current.toLowerCase(),
              '/api/auth/register',
              '/health',
              '/api/health'
            ];
            return publicPaths.some(p => path === p.toLowerCase() || path.endsWith(p.toLowerCase())) && !path.includes('/api/system/');
          })();

          if (isPublicRoute) {
            return Promise.reject(error);
          }

          // One recovery belongs to this lifecycle. A late response from a signed-out
          // actor may neither start a second recovery nor replace a newer one.
          if (!_isRecovering401) {
            if (Date.now() - _lastRecoveryTime < 2000 && tokenRef.current) {
              clearAxiosGetCoalesceCache();
              originalRequest._retry = true;
              return axios(originalRequest);
            }
            _isRecovering401 = true;
            const recovery = (async () => {
              try {
                const refreshFn = refreshAccessTokenRef.current;
                const success = refreshFn ? await refreshFn() : false;
                if (responseEpoch !== authSessionEpochRef.current) return null;
                if (success) {
                  _lastRecoveryTime = Date.now();
                  return tokenRef.current || COOKIE_ONLY_SESSION_MARKER;
                }
                return null;
              } catch { return null; }
            })().finally(() => {
              // Promise callbacks run after assignment, even when no refresh
              // handler is available or refresh fails synchronously.
              if (_recoveryPromise === recovery) {
                _isRecovering401 = false;
                _recoveryPromise = null;
              }
            });
            _recoveryPromise = recovery;
          }
          try {
            const recovered = await _recoveryPromise;
            if (responseEpoch !== authSessionEpochRef.current) return Promise.reject(error);
            if (recovered) {
              clearAxiosGetCoalesceCache();
              originalRequest._retry = true;
              clearHumanAuthorizationHeader(originalRequest.headers);
              return axios(originalRequest);
            }
          } catch { /* The ordinary logout path below remains authoritative. */ }

          // Recovery başarısız -> Kullanıcıyı logout yap
          // Ancak SADECE auth endpoint'lerinde değilse (döngüsel logout önleme)
          const isAuthEndpoint = originalRequest.url?.includes('/auth/') || false;

          if (responseEpoch !== authSessionEpochRef.current) return Promise.reject(error);

          if (!isAuthEndpoint) {
            // Token süresi doldu mesajı
            if (apiConfigRef.current?.onTokenExpired) {
              apiConfigRef.current.onTokenExpired();
            } else {
              // Default: logout
              logout();
            }
          }
        }

        return Promise.reject(error);
      }
    );

    // Interceptor'ları kaldırmak için temizleme fonksiyonu
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
    // token dependency kaldırıldı: Request interceptor artık tokenRef.current kullanıyor.
    // logout dependency eklendi: Interceptor logout fonksiyonunu kullanıyor.
    // language kaldırıldı: Interceptor artık languageRef.current kullanıyor.
    // Bu, interceptor geçiş penceresinde isteklerin kaybolmasını ve mutex tutarsızlığını önler.
    // apiConfig / endpoint'ler bilinç olarak bağımlılıkta yok: ref üzerinden okunur — uydu projelerde her render yenilenen inline config ile interceptor fırtınası önlenir.
    // refreshAccessToken kimliği de ref ile interceptor içinde kullanılır ([logout] dışındaki değişimler interceptor'ı sürekli koparmaz).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  // Migration durumu: sadece header veya body (x-migration-in-progress / migrationInProgress).
  // Lock varken diğer istekler 503 döner, overlay çıkar; işlem bitince gelen cevapla kapanır.
  useEffect(() => {
    if (!user || !isInitialized) {
      setMigrationInProgress(false);
    }
  }, [user, isInitialized]);

  useEffect(() => {
    if (!migrationInProgress || !user || !isInitialized) return;

    let cancelled = false;
    let timerId: number | null = null;

    const pollMigrationStatus = async () => {
      try {
        const response = await axios.get('/api/status/migration', {
          withCredentials: true,
          headers: {
            'Accept-Language': `${languageRef.current},en-US;q=0.9`,
          },
        });
        if (cancelled) return;
        if ((response.data?.data?.migrationInProgress ?? response.data?.migrationInProgress) === false) {
          setMigrationInProgress(false);
          return;
        }
      } catch {
        // 503/connection errors are already handled by the interceptor; keep polling quietly.
      }

      if (!cancelled) {
        timerId = window.setTimeout(pollMigrationStatus, 3000);
      }
    };

    timerId = window.setTimeout(pollMigrationStatus, 1500);

    return () => {
      cancelled = true;
      if (timerId != null) {
        window.clearTimeout(timerId);
      }
    };
  }, [migrationInProgress, user, isInitialized]);

  // Mount olduğunda başlat - token'ın yüklendiğinden ve header'ın ayarlandığından emin ol
  useEffect(() => {
    let cancelled = false;
    const sessionEpoch = authSessionEpochRef.current;
    const isCurrent = () => !cancelled && sessionEpoch === authSessionEpochRef.current;
    const watchdog = window.setTimeout(() => {
      if (!isCurrent()) return;
      setSessionVerificationUnavailable(true);
      setToken(null);
      tokenRef.current = null;
      setUser(null);
      setDbSize(null);
      setIsInitialized(true);
      _initializeAuthRunning = false;
    }, AUTH_INIT_WATCHDOG_MS);

    const initIdentityKey = [
      initialAuth?.user?.id ?? '',
      initialAuth?.user?.email ?? '',
      String(initialAuth?.isAuthenticated ?? ''),
    ].join('|');

    if (authInitCompletedRef.current && authInitIdentityRef.current === initIdentityKey) {
      window.clearTimeout(watchdog);
      return;
    }
    authInitIdentityRef.current = initIdentityKey;

    const initializeAuth = async () => {
      // StrictMode çift-invocation guard
      if (_initializeAuthRunning) {
        // StrictMode guard
        return;
      }
      _initializeAuthRunning = true;
      try {
        // 1. InitialAuth kontrolü (SSR/Template'den gelen)
        const authUser = initialAuth?.user;
        const authIsAuthenticated = initialAuth?.isAuthenticated;

        if (authUser && authIsAuthenticated && !explicitAccountChange && !signedOutAtMount) {
          if (isCurrent()) {
            // initialAuth source accepted
            setUser(applyProductPermissionContext(normalizeUserIdentity(authUser as User), null, {
              appCode: getApplicationCodeForApi(), companyId: null, applicationAuthMode: APPLICATION_AUTH_MODE,
            }));
            meSessionHydratedRef.current = true;
            if ((authUser as User).language) {
              const userLanguage = normalizeLanguage((authUser as User).language);
              setLanguage(userLanguage);
              persistLanguage(userLanguage);
            }
            const sessionMarker = toCookieOnlySessionMarker(initialAuth?.token);
            setToken(sessionMarker);
            tokenRef.current = sessionMarker;
            clearDefaultAuthorizationHeader();

            setIsInitialized(true);
            window.clearTimeout(watchdog);
            return;
          }
        }

        // 2. Cookie tabanlı oturum kontrolü (fetchCurrentUser)
        // Hesap değiştirme açık kullanıcı niyetidir; mevcut cookie ile formdan
        // otomatik çıkılmaz. Yeni parola girişi yine normal auth endpoint'ine gider.
        if (new URLSearchParams(window.location.search).get('account') === 'change'
          || parseWorkspaceNotice(readWorkspaceNotice())?.phase === 'signed-out') {
          if (isCurrent()) setIsInitialized(true);
          window.clearTimeout(watchdog);
          return;
        }
        // Her zaman /me dene: Platform session cookie JS tarafından okunamaz.
        // Platform'dan Core'a geçişte session cookie varsa, atlarsak login'e düşer.
        // Login sayfası da bir kez doğrulanır: JS HttpOnly oturumun yokluğunu
        // bilemez. Oturumsuz 401 normaldir; token yokken refresh başlatılmaz.
        let fetchedUser: User | null = null;
        try {
          fetchedUser = await fetchCurrentUser();
        } catch (e) {
          fetchedUser = null;
        }

        if (!isCurrent()) return;
        if (fetchedUser) {
          // Oturum geçerli
          setUser(fetchedUser);
          const sessionMarker = toCookieOnlySessionMarker();
          setToken(sessionMarker);
          tokenRef.current = sessionMarker;
          clearDefaultAuthorizationHeader();

          setIsInitialized(true);
          _initializeAuthRunning = false;
          window.clearTimeout(watchdog);
          return;
        }

        // qpi identity oturumu mevcut fakat qpa seçilmemiş olabilir. Browser
        // HttpOnly cookie'yi okuyamaz; same-origin BFF listeleme sonucu bu durumu
        // güvenli biçimde ayırt eder ve tek alanı otomatik seçer.
        if (APPLICATION_AUTH_MODE && isCurrent() && await discoverApplicationSpaces()) {
          _initializeAuthRunning = false;
          window.clearTimeout(watchdog);
          return;
        }

        // 3. Hiçbir yöntem başarılı olmadı -> Login'e yönlendir
        if (isCurrent()) {
          setToken(null);
          setUser(null);
          setDbSize(null);
          setIsInitialized(true);
          window.clearTimeout(watchdog);
        }

      } catch (error) {
        if (import.meta.env.MODE === 'development') {
          console.error('[initializeAuth] Unexpected error:', error);
        }
        if (isCurrent()) {
          setToken(null);
          setUser(null);
          setIsInitialized(true);
          window.clearTimeout(watchdog);
        }
      } finally {
        _initializeAuthRunning = false;
        if (isCurrent()) authInitCompletedRef.current = true;
        window.clearTimeout(watchdog);
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
      window.clearTimeout(watchdog);
      _initializeAuthRunning = false; // StrictMode cleanup'ta guard'ı sıfırla
    };
    // initialAuth dependency olarak eklenmeli, ancak her render'da çalışmaması için dikkatli olmalıyız
    // initialAuth objesinin referansı değişebilir, bu yüzden sadece içeriğini kontrol et
    // apiConfig/buildApiUrlWithConfig bağımlılıkta olmamalı — referans dalgalanması /me fırtınası üretir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialAuth?.user?.id,
    initialAuth?.user?.email,
    initialAuth?.isAuthenticated,
  ]);

  // Browser state yalnız credential olmayan cookie session marker'ı taşır.
  useEffect(() => {
    clearDefaultAuthorizationHeader();
    tokenRef.current = token === COOKIE_ONLY_SESSION_MARKER ? token : null;
  }, [token]);

  // Proactive token refresh: cookie-based token'da JS decode yapılamaz.
  // Token süresi dolduğunda 401 interceptor recovery ile otomatik yenilenir.

  // Fetch user if token exists but user missing
  useEffect(() => {
    if (!isInitialized) return;
    if (!token) return;
    if (user) return;
    if (meSessionHydratedRef.current) return;

    if (meHydrateTokenRef.current === token) return;
    meHydrateTokenRef.current = token;

    // Kimlik/rol JWT claim'inden türetilmez; gerçek user yalnız cookie ile /me'den gelir.
    void fetchCurrentUser();
    // fetchCurrentUser bağımlılıkta olmamalı: useCallback kimliği dalgalanınca effect sürekli tetiklenir
    // ve GET /api/auth/me fırtınası oluşur (yalnızca token + user geçişi).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, isInitialized]);

  // Login: sends browser timezone and locale for backend.
  const login = async (
    email: string,
    password: string,
    options: { spaceCode?: string } = {},
  ) => {
    if (logoutInFlightRef.current) await logoutInFlightRef.current;
    resetForWorkspaceSwitch();
    const sessionEpoch = authSessionEpochRef.current;
    try {
      loginStartMsRef.current = Date.now();
      setDatabaseError(null);
      setDatabaseInitializing(false);
      setDatabaseInitializationMessage('');
      debugAuthTiming('login:start', { email });
      // Browser timezone ve language'unu al ve login request'ine ekle
      const browserTimezone = getBrowserTimezone();
      const browserLanguage = getBrowserLanguage();

    const entryIntent = !APPLICATION_AUTH_MODE && typeof window !== 'undefined'
        ? readWorkspaceEntryIntent(window.location.href, getApplicationCodeForApi()) : null;
      const loginUrl = entryIntent && entryIntent.applicationCode !== 'APPS'
        ? `${entryIntent.root}/api/auth/login` : buildApiUrlWithConfig(loginEndpoint);
      // login debug log intentionally disabled

      const headers = { ...(apiConfig?.getHeaders?.() || {}) };
      clearHumanAuthorizationHeader(headers);

      let response;
      let loginData: {
        email: string;
        password: string;
        timezone?: string;
        language?: string;
        applicationCode?: string;
        spaceCode?: string;
      } = { email, password };
      try {
        // ...
        if (browserTimezone && !capturePresentation) {
          loginData.timezone = browserTimezone;
        }
        const languageToSend = (language && SUPPORTED_LANGUAGES.includes(language)) ? language : browserLanguage;
        if (!capturePresentation && languageToSend && SUPPORTED_LANGUAGES.includes(languageToSend as any)) {
          loginData.language = languageToSend;
        }
        if (APPLICATION_AUTH_MODE) {
          loginData.applicationCode = getApplicationCodeForApi();
          const spaceCode = String(options.spaceCode ?? '').trim();
          if (spaceCode) loginData.spaceCode = spaceCode;
        }

        response = await axios.post(loginUrl, loginData, { headers, timeout: 120000, signal: authRequestsRef.current.signal });
      } catch (requestError) {
        throw requestError;
      }

      const respData = response.data;
      if (sessionEpoch !== authSessionEpochRef.current) return false;
      // login debug log intentionally disabled
      debugAuthTiming('login:response', {
        durationMs: loginStartMsRef.current ? Date.now() - loginStartMsRef.current : undefined,
        hasUser: Boolean((respData?.data ?? respData)?.user),
        hasToken: Boolean((respData?.data ?? respData)?.token),
      });
      // SaaS login dönüşü success wrapper'lı veya doğrudan payload olabilir
      const payload = respData?.data ?? respData;
      const respSuccess = typeof respData?.success === 'boolean' ? respData.success : true;

      if (respSuccess && payload?.secondFactorRequired === true) {
        secondFactorEpochRef.current = sessionEpoch;
        setToken(null); tokenRef.current = null; setUser(null); clearDefaultAuthorizationHeader();
        throw Object.assign(new Error('İkinci doğrulamayı tamamlayın.'), { code: 'IDENTITY_SECOND_FACTOR_REQUIRED' });
      }
      if (respSuccess && (payload?.workspaceSelectionRequired === true || payload?.applicationSpaceSelectionRequired === true || payload?.user)) clearWorkspaceSignOutNotice();
      if (respSuccess && (payload?.workspaceSelectionRequired === true || (entryIntent && payload?.user))) {
        authSessionEpochRef.current += 1;
        setToken(null);
        tokenRef.current = null;
        setUser(null);
        setDbSize(null);
        setUserSettings(null);
        _lastAuthMeSnapshot = null;
        meSessionHydratedRef.current = false;
        fetchCurrentUserInFlightRef.current = null;
        clearDefaultAuthorizationHeader();
        setWorkspaceSelectionRequired(true);
        setIsInitialized(true);
        return false;
      }

      if (respSuccess && payload?.applicationSpaceSelectionRequired === true) {
        const spaces = normalizeApplicationSpaces(payload.applicationSpaces);
        if (spaces.length < 2) {
          throw new Error('Uygulama alanı seçim yanıtı geçersizdir.');
        }
        authSessionEpochRef.current += 1;
        setToken(null);
        tokenRef.current = null;
        setUser(null);
        setDbSize(null);
        setUserSettings(null);
        _lastAuthMeSnapshot = null;
        meSessionHydratedRef.current = false;
        fetchCurrentUserInFlightRef.current = null;
        clearDefaultAuthorizationHeader();
        applicationSpacesRef.current = spaces;
        setApplicationSpaces(spaces);
        setWorkspaceSelectionRequired(false);
        setApplicationSpaceSelectionRequired(true);
        setIsInitialized(true);
        return false;
      }

      if (respSuccess && payload?.user) {
        beginAuthenticatedWorkspace();
        // Legacy bir yanıt token taşısa dahi yok sayılır; browser oturumu
        // HttpOnly cookie ve /auth/me doğrulamasıyla devam eder.
        const {
          user: newUser,
          databaseInitializing,
          databaseInitializationMessage,
          databaseError
        } = payload;

        if (databaseError) {
          setDatabaseError(databaseError);
          setDatabaseInitializing(false);
          setDatabaseInitializationMessage('');
        } else if (databaseInitializing) {
          const t = getTranslations(language as SupportedLanguage);
          const message = databaseInitializationMessage || t.common.serverConfiguring;
          setDatabaseInitializing(true);
          setDatabaseInitializationMessage(message);
          setDatabaseError(null);
        } else {
          setDatabaseInitializing(false);
          setDatabaseInitializationMessage('');
          setDatabaseError(null);
        }

        const normalizedLoginUser = applyLoginEmailFallback(
          normalizeUserIdentity(newUser as User),
          email
        );
        const sessionMarker = toCookieOnlySessionMarker(payload?.token);
        setToken(sessionMarker);
        tokenRef.current = sessionMarker;
        clearDefaultAuthorizationHeader();

        const languageToSend = (language && SUPPORTED_LANGUAGES.includes(language)) ? language : browserLanguage;
        const userLanguage = normalizeLanguage(
          (typeof languageToSend === 'string' && SUPPORTED_LANGUAGES.includes(languageToSend as any))
            ? languageToSend
            : (newUser?.language || browserLanguage)
        );
        setLanguage(userLanguage);
        persistLanguage(userLanguage);

        const tryRefreshThenMe = async () => {
          if (sessionEpoch !== authSessionEpochRef.current) return null;
          if (!APPLICATION_AUTH_MODE) {
            try {
              await axios.post(buildApiUrlWithConfig(refreshEndpoint), {}, {
                headers: apiConfigRef.current?.getHeaders?.() || {}, signal: authRequestsRef.current.signal,
              });
            } catch {
              // refresh opsiyonel; /me cookie ile de çalışır
            }
          }
          if (sessionEpoch !== authSessionEpochRef.current) return null;
          const hydratedUser = await hydrateMeOnceAfterLogin();
          return hydratedUser;
        };

        if (databaseInitializing) {
          const pollCustomerId = payload.user?.customerId as string | undefined;
          const dbStatusUrl = buildApiUrlWithConfig(
            `${DEFAULT_AUTH_DB_STATUS_PATH}${pollCustomerId ? `?customerId=${encodeURIComponent(pollCustomerId)}` : ''}`
          );
          if (dbStatusPollRef.current) clearInterval(dbStatusPollRef.current);
          dbStatusPollRef.current = setInterval(async () => {
            try {
              const resp = await axios.get(dbStatusUrl, { withCredentials: true, signal: authRequestsRef.current.signal });
              if (sessionEpoch !== authSessionEpochRef.current) return;
              const statusData = resp.data?.data ?? {};
              if (statusData.message) {
                setDatabaseInitializationMessage(statusData.message);
              }
              if (statusData.status === 'ready') {
                if (dbStatusPollRef.current) {
                  clearInterval(dbStatusPollRef.current);
                  dbStatusPollRef.current = null;
                }
                const hydratedUser = await tryRefreshThenMe();
                if (sessionEpoch !== authSessionEpochRef.current) return;
                if (!hydratedUser) {
                  setToken(null);
                  tokenRef.current = null;
                  setUser(null);
                  setDbSize(null);
                  meSessionHydratedRef.current = false;
                  fetchCurrentUserInFlightRef.current = null;
                  clearDefaultAuthorizationHeader();
                  setDatabaseError('Oturum doğrulanamadı. Lütfen tekrar giriş yapın.');
                  setDatabaseInitializing(false);
                  setDatabaseInitializationMessage('');
                  return;
                }
                setDatabaseInitializing(false);
                setDatabaseInitializationMessage('');
                setDatabaseError(null);
              } else if (statusData.status === 'error') {
                if (dbStatusPollRef.current) {
                  clearInterval(dbStatusPollRef.current);
                  dbStatusPollRef.current = null;
                }
                setDatabaseError(getDatabaseStatusErrorMessage(statusData, languageRef.current as SupportedLanguage));
                setDatabaseInitializing(false);
                setDatabaseInitializationMessage('');
              }
            } catch {
              // polling devam
            }
          }, 3000);
        } else {
          const hydratedUser = await tryRefreshThenMe();
          if (sessionEpoch !== authSessionEpochRef.current) return false;
          if (!hydratedUser) {
            setToken(null);
            tokenRef.current = null;
            setUser(null);
            setDbSize(null);
            meSessionHydratedRef.current = false;
            fetchCurrentUserInFlightRef.current = null;
            clearDefaultAuthorizationHeader();
            throw new Error('Oturum doğrulanamadı. Lütfen tekrar giriş yapın.');
          }
          debugAuthTiming('login:setUser(cookieVerified)', {
            durationMs: loginStartMsRef.current ? Date.now() - loginStartMsRef.current : undefined,
            firstName: hydratedUser.firstName || normalizedLoginUser.firstName || '(empty)',
            lastName: hydratedUser.lastName || normalizedLoginUser.lastName || '(empty)',
          });
        }

        if (sessionEpoch !== authSessionEpochRef.current) return false;
        if (onLogin) {
          onLogin({ token: sessionMarker, user: newUser as User });
        }
        if (onTokenChange) {
          onTokenChange({ token: sessionMarker, user: newUser as User });
        }
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
      return true;
    } catch (error: any) {
      if (sessionEpoch !== authSessionEpochRef.current) return false;
      // This is a successful password step awaiting the user's second factor,
      // not a transport failure. Keep its typed continuation for LoginForm.
      if (error.code === 'IDENTITY_SECOND_FACTOR_REQUIRED') throw error;
      // onError callback'i çağır
      if (apiConfig?.onError) {
        apiConfig.onError(error);
      }

      if (import.meta.env.MODE === 'development') {
        console.error('❌ Login error:', error);
        console.error('❌ Login error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        });
        if (error.response?.data) {
          console.error('❌ Error response data:', JSON.stringify(error.response.data, null, 2));
        }
      }

      const errorResponse = error.response?.data;
      const status = error.response?.status;
      const t = getTranslations(language as SupportedLanguage);

      // Önce userMessage'ı kontrol et (interceptor'dan geliyor)
      let errorMessage = error.userMessage
        || errorResponse?.message
        || errorResponse?.error
        || error.message
        || (t.login?.loginFailed ?? 'Giriş başarısız');

      // Error logging (rehber gereksinimi)
      if (import.meta.env.MODE === 'development') {
        console.error('Login error:', {
          message: errorMessage,
          status: error.response?.status,
          data: error.response?.data,
        });
      }

      // CLOUD/SaaS 401 davranışı genel kimlik bilgisi mesajını korur. Yalnız Core'un
      // kodlu LOCAL kilit cevabı, kullanıcıya kilit durumunu açıkça bildirir.
      if (status === 401) {
        errorMessage = errorResponse?.code === LOCAL_ACCOUNT_LOCKED_CODE
          ? (t.login?.errors?.accountLocked ?? errorMessage)
          : (t.login?.errors?.invalidCredentials ?? errorMessage);
      }

      // 403 Forbidden
      if (status === 403) {
        errorMessage = t.common?.forbidden ?? errorMessage;
      }

      // 431 hatası: Request Header Fields Too Large
      if (status === 431) {
        if (import.meta.env.MODE === 'development') {
          console.warn('⚠️ 431 hatası: Header boyutu çok büyük. LocalStorage temizleniyor...');
        }
        localStorage.clear();
        clearDefaultAuthorizationHeader();
        errorMessage = t.login?.errors?.sessionTooLarge ?? errorMessage;
      }

      // Network error (seçili dile göre çeviri)
      if (!error.response) {
        if (error.message?.includes('Proxy error') || error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT') {
          errorMessage = import.meta.env.MODE === 'development'
            ? `Backend sunucusuna bağlanılamadı. Lütfen backend sunucusunun çalıştığından emin olun. API portu: ${API_PORT}.\n\nBackend'i başlatmak için: npm run dev (root dizinde) veya cd apps/api && npm run dev`
            : (t.login?.backendConnectFailed ?? t.common?.network ?? errorMessage);
        } else {
          errorMessage = t.login?.backendConnectFailed ?? t.common?.network ?? errorMessage;
        }
      }

      // 503 Service Unavailable
      if (error.response?.status === 503) {
        const errorData = error.response?.data;
        if (isDatabaseUnavailableError(error)) {
          errorMessage = getDatabaseUnavailableMessage(error, language as SupportedLanguage);
          setDatabaseError(errorMessage);
          setDatabaseInitializing(false);
          setDatabaseInitializationMessage('');
          setMigrationInProgress(false);
        } else {
          errorMessage =
            errorData?.message ||
            errorData?.error ||
            error.userMessage ||
            errorMessage ||
            t.login?.backendUnavailable;
        }
      }

      // Validation errors varsa detayları göster (Zod issues dizisi veya alan haritası)
      if (errorResponse?.errors != null) {
        const errorDetails = formatApiValidationErrors(errorResponse.errors);
        if (errorDetails) {
          errorMessage = `${errorMessage} - ${errorDetails}`;
        }
      }

      throw new Error(errorMessage);
    }
  };

  // Update UI language and persist to API when authenticated.
  const updateLanguage = useCallback(async (newLanguage: SupportedLanguage) => {
    if (capturePresentation) return;
    const normalized = normalizeLanguage(newLanguage);
    const sessionEpoch = authSessionEpochRef.current;

    setLanguage((prev) => {
      if (prev === normalized) return prev;
      persistLanguage(normalized);
      return normalized;
    });

    if (user && !APPLICATION_AUTH_MODE) {
      try {
        const headers = apiConfigRef.current?.getHeaders?.() || {};
        const persistence = await persistUserLanguage({
          persistCanonical: () => axios.patch('/api/settings/user', {
            language: normalized,
          }, { headers }),
          persistLegacy: languageEndpoint ? () => axios.patch(buildApiUrlWithConfig(languageEndpoint), {
            language: normalized,
          }, { headers }) : undefined,
        });
        setUserSettings(prev => prev ? { ...prev, language: normalized } : { language: normalized });

        if (sessionEpoch !== authSessionEpochRef.current) return;

        if (persistence.source === 'canonical-settings') {
          const updatedUser = normalizeUserIdentity({ ...user, language: normalized });
          setUser(updatedUser);
          if (onTokenChange) {
            onTokenChange({ token, user: updatedUser });
          }
          return;
        }

        const response = persistence.response;
        if (response.data?.success && response.data?.data) {
          // A legacy language endpoint may return the central identity profile;
          // rehydrate through the same product/company permission boundary.
          _lastAuthMeSnapshot = null;
          const updatedUser = await fetchCurrentUser();
          if (updatedUser && onTokenChange) {
            onTokenChange({ token, user: updatedUser });
          }
        }
      } catch (error: any) {
        if (apiConfigRef.current?.onError) {
          apiConfigRef.current.onError(error);
        } else if (import.meta.env.MODE === 'development') {
          console.error('Sunucuda dil güncellenemedi:', error);
        }
      }
    }
  }, [token, user, onTokenChange, languageEndpoint, buildApiUrlWithConfig, capturePresentation, setLanguage, fetchCurrentUser]);

  // User değiştiğinde language'ı güncelle (gereksiz setState → alt bileşen effect zincirini keser)
  useEffect(() => {
    if (!user) return;
    if (user.language) {
      const userLanguage = normalizeLanguage(user.language);
      setLanguage((prev) => {
        if (prev === userLanguage) return prev;
        persistLanguage(userLanguage);
        return userLanguage;
      });
      return;
    }
    const saved = readSavedLanguage();
    if (saved) {
      setLanguage((prev) => (prev === saved ? prev : saved));
    }
  }, [user?.id, user?.language]);

  const refreshUser = useCallback(async () => {
    _lastAuthMeSnapshot = null;
    return fetchCurrentUser();
  }, [fetchCurrentUser]);

  const updateUserAvatar = useCallback((avatar: string | null) => {
    setUser(prev => prev ? { ...prev, avatar } : prev);
  }, []);

  const updateUserSettingsInContext = useCallback((settings: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...settings } : prev);
  }, []);

  /** Tek veya birden fazla ayar anahtarını güncelle (PATCH sonrası context sync) */
  const patchUserSettings = useCallback((patch: Record<string, unknown>) => {
    setUserSettings(prev => prev ? { ...prev, ...patch } : patch);
  }, []);

  const clearDatabaseInitializing = useCallback(() => {
    setDatabaseInitializing(false);
    setDatabaseInitializationMessage('');
    setDatabaseError(null);
  }, []);



  return (
    <AuthContext.Provider
      value={{
        sessionVerificationUnavailable,
        user,
        token,
        // User varsa authenticated say; token olmayabilir, session cookie ile doğrulanmış olabilir
        isAuthenticated: isInitialized && !!user,
        isInitialized,
        language,
        dbSize,
        userSettings,
        databaseInitializing,
        databaseInitializationMessage,
        databaseError,
        migrationInProgress,
        workspaceSelectionRequired,
        finishSecondFactor: (requiresSelection: boolean) => {
          if (logoutInFlightRef.current || secondFactorEpochRef.current !== authSessionEpochRef.current) return;
          secondFactorEpochRef.current = null;
          clearWorkspaceSignOutNotice();
          if (requiresSelection) setWorkspaceSelectionRequired(true);
          else window.location.reload();
        },
        applicationSpaceSelectionRequired,
        applicationSpaces,
        login,
        logout,
        updateLanguage,
        applyLanguageLocal,
        refreshUser,
        updateUserAvatar,
        updateUserSettingsInContext,
        patchUserSettings,
        clearDatabaseInitializing,
        resetForWorkspaceSwitch,
        selectApplicationSpace,
      }}
    >
      {sessionVerificationUnavailable && !user ? <AuthSessionUnavailable language={language} onRetry={() => window.location.reload()} /> : (
      <TenantProvider
        auth={{
          user,
          isInitialized,
          language,
          workspaceSelectionRequired,
          resetForWorkspaceSwitch,
          logout,
        }}
      >
        {applicationSpaceSelectionRequired ? (
          <ApplicationSpaceChooser
            spaces={applicationSpaces}
            language={language}
            onSelect={selectApplicationSpace}
          />
        ) : children}
      </TenantProvider>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
